import { useMarketConfig } from '~/app/market-config';
import { marketTo3DigitCountryCode } from '~/app/utils/market-to-3digit-countrycode';

export interface PlaceIndex {
	PlaceId: string;
	Text: string;
}

export function useLocationSearch() {
	const stage = 'prod';

	interface Place {
		Country: string;
		Geometry: {
			Point: [number, number];
		};
		Interpolated: boolean;
		Label: string;
		PostalCode: string;
		Region: string;
		Municipality: string;
		SubRegion: string;
		Neighborhood?: string;
	}

	interface GeoLocation {
		Distance: number;
		Place: Place;
	}

	const { market, language } = useMarketConfig();

	const filterCountries = marketTo3DigitCountryCode[market];
	const apiKey = process.env.AWS_LOCATION_ACCESS_KEY; // for local development you have to insert the real key here
	const fetchOptions: RequestInit = {
		method: 'GET',

		// @ts-ignore
		headers: {
			'x-api-key': apiKey,
		},
	};

	const getLocationByCoordinates = async (position: number[]) => {
		const params = new URLSearchParams({
			position: `[${position}]`, // needs to be an "array'-string but not an array object
			filterCountries,
			language,
		}).toString();

		try {
			const response = await fetch(
				`https://fa-location-search.api.${stage}.collab.apps.one.audi/api/locationsearch?${params}`,
				fetchOptions,
			);

			const data: GeoLocation[] = await response.json();
			const {
				Place: { Geometry, Label: label },
			} = data[0];

			return {
				coordinates: {
					lat: Geometry.Point[1],
					lng: Geometry.Point[0],
				},
				label,
			};
		} catch (error) {
			console.error('==== getLocationByCoordinates is dark and full of errors', error);
		}
	};

	const getPlaceIdsBySearchTerm = async (text: string) => {
		const params = new URLSearchParams({
			text,
			filterCountries,
			language,
			maxResults: '7',
		}).toString();

		try {
			const response = await fetch(
				`https://fa-location-search.api.${stage}.collab.apps.one.audi/api/locationsearch?${params}`,
				fetchOptions,
			);
			const data: PlaceIndex[] = await response.json(); // Extracting data as a JSON Object from the response

			if (!data) return [];

			// removing any addresses which do not have a placeId
			const filterLocationResponse: PlaceIndex[] = data.filter(
				(item: PlaceIndex) => item.Text !== undefined && item.PlaceId !== undefined,
			);

			return filterLocationResponse;
		} catch (error) {
			console.error('==== getPlaceIdsBySearchTerm is dark and full of errors', error);
		}
	};

	const getCoordinatesByPlaceId = async (placeId: string) => {
		const params = new URLSearchParams({
			placeId,
		}).toString();

		try {
			const response = await fetch(
				`https://fa-location-search.api.${stage}.collab.apps.one.audi/api/locationsearch?${params}`,
				fetchOptions,
			);
			const data: Place = await response.json();
			const { Geometry, Neighborhood, Municipality } = data;

			return {
				coordinates: {
					lat: Geometry.Point[1],
					lng: Geometry.Point[0],
				},
				neighborhood: Neighborhood,
				region: Municipality,
			};
		} catch (error) {
			console.error('==== getCoordinatesByPlaceId is dark and full of errors', error);
		}
	};

	return { getPlaceIdsBySearchTerm, getCoordinatesByPlaceId, getLocationByCoordinates };
}
